import { pickLocalizedText, resolveTranslationLanguage, type MivoTranslationKey, type MivoTranslationType } from "@mivo/i18n";
import type { LegalSection } from "./legalDocuments";

export type LegalPlanTrialTermsInput = {
    name: string;
    trialDays: number;
    patientLimit: number | null;
};

const PROFESSIONAL_TRIAL_SECTION_TITLES: MivoTranslationType = {
    "pt-BR": "5.1 Período de Experimentação (profissionais)",
    en: "5.1 Trial period (professionals)",
    es: "5.1 Período de prueba (profesionales)",
    de: "5.1 Testzeitraum (Fachkräfte)",
};

function resolveLocale(locale?: string | null): MivoTranslationKey {
    return resolveTranslationLanguage(locale);
}

export function formatTrialDaysLabel(trialDays: number, locale?: string | null): string {
    const resolvedLocale = resolveLocale(locale);
    if (resolvedLocale === "en") {
        return `Try it for ${trialDays} ${trialDays === 1 ? "day" : "days"}`;
    }
    if (resolvedLocale === "es") {
        return `Pruébalo por ${trialDays} ${trialDays === 1 ? "día" : "días"}`;
    }
    if (resolvedLocale === "de") {
        return `Testen Sie ${trialDays} ${trialDays === 1 ? "Tag" : "Tage"}`;
    }

    return `Experimente por ${trialDays} ${trialDays === 1 ? "dia" : "dias"}`;
}

function formatPatientLimitText(patientLimit: number | null, locale?: string | null): string {
    const resolvedLocale = resolveLocale(locale);
    if (resolvedLocale === "en") {
        return patientLimit == null
            ? "unlimited active patients"
            : `a limit of ${patientLimit} active patients`;
    }
    if (resolvedLocale === "es") {
        return patientLimit == null
            ? "pacientes activos ilimitados"
            : `un límite de ${patientLimit} pacientes activos`;
    }
    if (resolvedLocale === "de") {
        return patientLimit == null
            ? "unbegrenzt viele aktive Patienten"
            : `ein Limit von ${patientLimit} aktiven Patienten`;
    }

    return patientLimit == null
        ? "pacientes ativos ilimitados"
        : `limite de ${patientLimit} pacientes ativos`;
}

export function buildProfessionalTrialTermsParagraph(
    plans: readonly LegalPlanTrialTermsInput[],
    locale?: string | null,
): string {
    const resolvedLocale = resolveLocale(locale);
    const trialPlans = plans.filter((plan) => plan.trialDays > 0);
    if (trialPlans.length === 0) {
        if (resolvedLocale === "en") {
            return "Professional accounts follow the limits and conditions of the contracted plan or the current plan published on the platform catalog. When a trial period exists, after that period counted from account creation, access may be blocked until a paid plan is purchased, according to the conditions published on the platform.";
        }
        if (resolvedLocale === "es") {
            return "Las cuentas de profesionales siguen los límites y condiciones del plan contratado o vigente en la plataforma, según lo publicado en el catálogo de planes. Cuando exista un período de prueba, una vez finalizado ese plazo contado desde la creación de la cuenta, el acceso podrá bloquearse hasta la contratación de un plan de pago, según las condiciones publicadas en la plataforma.";
        }
        if (resolvedLocale === "de") {
            return "Professional accounts follow the limits and conditions of the contracted plan or the current plan published on the platform catalog. When a trial period exists, after that period counted from account creation, access may be blocked until a paid plan is purchased, according to the conditions published on the platform.";
        }

        return "Contas de profissionais seguem os limites e condições do plano contratado ou vigente na plataforma, conforme divulgado no catálogo de planos. Quando houver período de experimentação, após esse prazo contado da criação da conta o acesso poderá ser bloqueado até a contratação de plano pago, conforme condições divulgadas na plataforma.";
    }

    if (trialPlans.length === 1) {
        const plan = trialPlans[0];
        if (resolvedLocale === "en") {
            return `Professional accounts linked to the ${plan.name} plan start in the "${formatTrialDaysLabel(plan.trialDays, resolvedLocale)}" period, with ${formatPatientLimitText(plan.patientLimit, resolvedLocale)}. After the trial period counted from account creation, access may be blocked until a paid plan is purchased, according to the conditions published on the platform.`;
        }
        if (resolvedLocale === "es") {
            return `Las cuentas de profesionales vinculadas al plan ${plan.name} comienzan en el período "${formatTrialDaysLabel(plan.trialDays, resolvedLocale)}", con ${formatPatientLimitText(plan.patientLimit, resolvedLocale)}. Una vez finalizado el período de prueba contado desde la creación de la cuenta, el acceso podrá bloquearse hasta la contratación de un plan de pago, según las condiciones publicadas en la plataforma.`;
        }
        if (resolvedLocale === "de") {
            return `Professional accounts linked to the ${plan.name} plan start in the "${formatTrialDaysLabel(plan.trialDays, resolvedLocale)}" period, with ${formatPatientLimitText(plan.patientLimit, resolvedLocale)}. After the trial period counted from account creation, access may be blocked until a paid plan is purchased, according to the conditions published on the platform.`;
        }

        return `Contas de profissionais vinculadas ao plano ${plan.name} iniciam no período "${formatTrialDaysLabel(plan.trialDays, resolvedLocale)}", com ${formatPatientLimitText(plan.patientLimit, resolvedLocale)}. Após o prazo de experimentação contado da criação da conta, o acesso poderá ser bloqueado até a contratação de plano pago, conforme condições divulgadas na plataforma.`;
    }

    const planSummaries = trialPlans
        .map(
            (plan) =>
                `${plan.name}: "${formatTrialDaysLabel(plan.trialDays, resolvedLocale)}", ${formatPatientLimitText(plan.patientLimit, resolvedLocale)}`,
        )
        .join("; ");

    if (resolvedLocale === "en") {
        return `Professional accounts may start in a trial period according to the plan selected at registration (${planSummaries}). After the trial period counted from account creation, access may be blocked until a paid plan is purchased, according to the conditions published on the platform.`;
    }
    if (resolvedLocale === "es") {
        return `Las cuentas de profesionales pueden comenzar con un período de prueba conforme al plan vigente en el registro (${planSummaries}). Una vez finalizado el período de prueba contado desde la creación de la cuenta, el acceso podrá bloquearse hasta la contratación de un plan de pago, según las condiciones publicadas en la plataforma.`;
    }
    if (resolvedLocale === "de") {
        return `Professional accounts may start in a trial period according to the plan selected at registration (${planSummaries}). After the trial period counted from account creation, access may be blocked until a paid plan is purchased, according to the conditions published on the platform.`;
    }

    return `Contas de profissionais podem iniciar em período de experimentação conforme o plano vigente no cadastro (${planSummaries}). Após o prazo de experimentação contado da criação da conta, o acesso poderá ser bloqueado até a contratação de plano pago, conforme condições divulgadas na plataforma.`;
}

export const PROFESSIONAL_TRIAL_TERMS_FALLBACK =
    "Contas de profissionais iniciam em período de experimentação conforme o plano vigente divulgado na plataforma, com limite de pacientes ativos definido nesse plano. Após o prazo de experimentação contado da criação da conta, o acesso poderá ser bloqueado até a contratação de plano pago, conforme condições divulgadas na plataforma.";

export function getTermsSections(
    baseSections: readonly LegalSection[],
    plans?: readonly LegalPlanTrialTermsInput[],
    locale?: string | null,
): LegalSection[] {
    const resolvedLocale = resolveLocale(locale);
    const trialParagraph =
        plans && plans.length > 0
            ? buildProfessionalTrialTermsParagraph(plans, resolvedLocale)
            : buildProfessionalTrialTermsParagraph([], resolvedLocale);

    return baseSections.map((section) =>
        section.title === pickLocalizedText(PROFESSIONAL_TRIAL_SECTION_TITLES, resolvedLocale)
            ? { ...section, paragraphs: [trialParagraph] }
            : section,
    );
}
